import { useState } from 'react';
import { format, set } from 'date-fns';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';
import { useNotifications } from '@/hooks/use-notifications';
import TimePicker from '@/components/common/time-picker';
import {
  useAdjustConfidenceTimeMutation,
  useClearConfidenceMessageMutation,
  useConfidenceControlQuery,
  useConfidenceMessageMutation,
  useConfirmConfidenceSuggestionMutation,
  useDismissConfidenceSuggestionMutation,
} from '@/state/queries/player/use-confidence';

const AUTOMATIC_PRESET_KEYS = new Set(['five_minutes', 'wrap_up', 'time_up']);
const CLOCK_24H = /^([01]\d|2[0-3]):([0-5]\d)$/;

type Props = {
  open: boolean;
  panel: 'time' | 'message';
  onClose: () => void;
  entityType: 'Flow' | 'Space';
  entityId: string;
  slotId?: string;
  sectionTitle: string;
};

export function ConfidenceControlOverlay({ open, panel, onClose, entityType, entityId, slotId, sectionTitle }: Props) {
  const t = useTranslations();
  const notification = useNotifications();
  const entity = { entityType, entityId };
  const { data } = useConfidenceControlQuery(open ? entity : undefined);
  const sendMessage = useConfidenceMessageMutation();
  const clearMessage = useClearConfidenceMessageMutation();
  const adjustTime = useAdjustConfidenceTimeMutation();
  const confirmSuggestion = useConfirmConfidenceSuggestionMutation();
  const dismissSuggestion = useDismissConfidenceSuggestionMutation();
  const [text, setText] = useState('');
  const [mode, setMode] = useState<'remaining' | 'fromStart' | 'untilClock'>('remaining');
  const [minutesValue, setMinutesValue] = useState('10');
  const [untilValue, setUntilValue] = useState<Date | null>(null);

  const presets = [
    ...(data?.systemPresets ?? []).filter((preset) => !AUTOMATIC_PRESET_KEYS.has(preset.key)),
    ...(data?.orgPresets ?? []).map((preset) => ({
      key: preset.id,
      text: preset.text,
      source: 'organization' as const,
    })),
  ];

  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const untilText = untilValue && !Number.isNaN(untilValue.getTime()) ? format(untilValue, 'HH:mm') : '';
  const laterTodayPossible = nowMinutes < 23 * 60 + 59;
  const earliestUntil = set(now, {
    hours: Math.floor((nowMinutes + 1) / 60),
    minutes: (nowMinutes + 1) % 60,
    seconds: 0,
    milliseconds: 0,
  });
  const latestUntil = set(now, { hours: 23, minutes: 59, seconds: 0, milliseconds: 0 });
  const untilValid = laterTodayPossible && isLaterToday(untilText, nowMinutes);
  const minutesNumber = Number(minutesValue);
  const minutesValid = minutesValue.trim() !== '' && Number.isFinite(minutesNumber) && minutesNumber >= 0;
  const timeValid = mode === 'untilClock' ? untilValid : minutesValid;

  const run = (request: Promise<unknown>, closeOnSuccess = false) => {
    void request.then(
      () => {
        if (closeOnSuccess) onClose();
      },
      () => notification.notifyError(t('flows.detail.control.confidence.actionError' as TranslationKey)),
    );
  };

  const applyTime = () => {
    if (!slotId || !timeValid) return;
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const seconds = Math.round(minutesNumber * 60);
    const request =
      mode === 'untilClock'
        ? adjustTime.mutateAsync({ ...entity, slotId, mode, untilClock: untilText, nowMinutes })
        : adjustTime.mutateAsync({
            ...entity,
            slotId,
            mode,
            ...(mode === 'remaining' ? { remainingSec: seconds } : { totalFromStartSec: seconds }),
            nowMinutes,
          });
    run(request, true);
  };

  const send = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    run(sendMessage.mutateAsync({ ...entity, text: trimmed }), true);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{sectionTitle}</DialogTitle>
      <DialogContent>
        {panel === 'time' ? (
          <Stack spacing={2}>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={mode}
              onChange={(_, value) => value && setMode(value)}
            >
              <ToggleButton value="remaining">
                {t('flows.detail.control.confidence.remaining' as TranslationKey)}
              </ToggleButton>
              <ToggleButton value="fromStart">
                {t('flows.detail.control.confidence.fromStart' as TranslationKey)}
              </ToggleButton>
              <ToggleButton value="untilClock">
                {t('flows.detail.control.confidence.until' as TranslationKey)}
              </ToggleButton>
            </ToggleButtonGroup>
            {mode === 'untilClock' ? (
              <TimePicker
                label={t('flows.detail.control.confidence.until' as TranslationKey)}
                value={untilValue}
                minTime={earliestUntil}
                maxTime={latestUntil}
                disabled={!laterTodayPossible}
                onChange={(value) => setUntilValue(value)}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 160 },
                    error: untilValue != null && !untilValid,
                    helperText:
                      untilValue != null && !untilValid
                        ? t('flows.detail.control.confidence.untilPast' as TranslationKey)
                        : undefined,
                  },
                }}
              />
            ) : (
              <StepperField
                label={t('flows.detail.control.confidence.minutes' as TranslationKey)}
                value={minutesValue}
                error={minutesValue.trim() !== '' && !minutesValid}
                onChange={setMinutesValue}
                formatInput={(raw) => raw.replace(/\D/g, '')}
                onStep={(delta) => setMinutesValue(stepMinutes(minutesValue, delta))}
              />
            )}
            <Typography variant="body2" color="text.secondary">
              {t('flows.detail.control.confidence.strategyHint' as TranslationKey)}
            </Typography>
            {data?.pendingSuggestion ? (
              <Alert
                severity="info"
                action={
                  <Stack direction="row" spacing={1}>
                    <Button color="inherit" size="small" onClick={() => run(confirmSuggestion.mutateAsync(entity))}>
                      {t('flows.detail.control.confidence.confirm' as TranslationKey)}
                    </Button>
                    <Button color="inherit" size="small" onClick={() => run(dismissSuggestion.mutateAsync(entity))}>
                      {t('flows.detail.control.confidence.dismiss' as TranslationKey)}
                    </Button>
                  </Stack>
                }
              >
                {data.pendingSuggestion.summary}
              </Alert>
            ) : null}
            <Button variant="contained" disabled={!slotId || !timeValid || adjustTime.isPending} onClick={applyTime}>
              {t('flows.detail.control.confidence.applyTime' as TranslationKey)}
            </Button>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              {presets.map((preset) => (
                <Chip key={preset.key} label={preset.text} onClick={() => setText(preset.text)} />
              ))}
            </Stack>
            <TextField
              multiline
              minRows={2}
              label={t('flows.detail.control.confidence.compose' as TranslationKey)}
              value={text}
              onChange={(event) => setText(event.target.value)}
            />
            {text ? (
              <Typography
                sx={{ px: 1.5, py: 1, borderRadius: 1, bgcolor: 'grey.900', color: 'common.white', fontWeight: 650 }}
              >
                {text}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={1}>
              <Button variant="contained" disabled={!text.trim() || sendMessage.isPending} onClick={send}>
                {t('flows.detail.control.confidence.send' as TranslationKey)}
              </Button>
              <Button disabled={clearMessage.isPending} onClick={() => run(clearMessage.mutateAsync(entity))}>
                {t('flows.detail.control.confidence.clear' as TranslationKey)}
              </Button>
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close' as TranslationKey)}</Button>
      </DialogActions>
    </Dialog>
  );
}

function stepMinutes(value: string, delta: number): string {
  const current = Number(value);
  const base = value.trim() !== '' && Number.isFinite(current) ? current : 0;
  return String(Math.max(0, base + delta));
}

function clockToMinutes(value: string): number | undefined {
  const match = CLOCK_24H.exec(value);
  if (!match) return undefined;
  return Number(match[1]) * 60 + Number(match[2]);
}

function isLaterToday(value: string, nowMinutes: number): boolean {
  const until = clockToMinutes(value);
  return until != null && Number.isFinite(until) && until > nowMinutes;
}

function StepperField({
  label,
  value,
  placeholder,
  error,
  helperText,
  onChange,
  formatInput,
  onStep,
}: {
  label: string;
  value: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  onChange: (value: string) => void;
  formatInput: (raw: string) => string;
  onStep: (delta: number) => void;
}) {
  return (
    <Stack spacing={0.5} sx={{ width: 'fit-content', maxWidth: '100%' }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
        <TextField
          size="small"
          hiddenLabel
          value={value}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          onChange={(event) => onChange(formatInput(event.target.value))}
          sx={{ width: placeholder ? 112 : 88 }}
          slotProps={{
            htmlInput: {
              inputMode: 'numeric',
              pattern: '[0-9]*',
              enterKeyHint: 'done',
            },
          }}
        />
        <Stack>
          <IconButton
            size="small"
            aria-label="Increase"
            onPointerDown={(event) => {
              event.preventDefault();
              onStep(1);
            }}
          >
            <KeyboardArrowUp fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Decrease"
            onPointerDown={(event) => {
              event.preventDefault();
              onStep(-1);
            }}
          >
            <KeyboardArrowDown fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
    </Stack>
  );
}
